import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AuthLayout, { AuthField } from "../components/auth/AuthLayout";
import { useLanguage } from "../context/LanguageContext";
import { forgotPassword } from "../api/auth";
import "../styles/Auth.scss";

const RESEND_COOLDOWN_SECONDS = 60;

const formatCountdown = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return `${minutes}:${String(remainder).padStart(2, "0")}`;
};

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [resendStatus, setResendStatus] = useState("idle"); // idle | submitting | error
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [lastRequestEmail, setLastRequestEmail] = useState("");
  const { t } = useLanguage();

  useEffect(() => {
    if (cooldownSeconds <= 0) {
      return undefined;
    }

    const timer = setTimeout(() => {
      setCooldownSeconds((prev) => Math.max(prev - 1, 0));
    }, 1000);

    return () => clearTimeout(timer);
  }, [cooldownSeconds]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const normalizedEmail = email.trim().toLowerCase();

    if (!normalizedEmail) {
      return;
    }

    if (cooldownSeconds > 0 && normalizedEmail === lastRequestEmail) {
      setStatus("success");
      return;
    }

    try {
      setStatus("submitting");

      await forgotPassword(email.trim());

      setLastRequestEmail(normalizedEmail);
      setCooldownSeconds(RESEND_COOLDOWN_SECONDS);
      setResendStatus("idle");
      setStatus("success");
    } catch (error) {
      setStatus("error");
    }
  };

  const handleResend = async () => {
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail || cooldownSeconds > 0 || resendStatus === "submitting") {
      return;
    }

    try {
      setResendStatus("submitting");
      await forgotPassword(email.trim());
      setLastRequestEmail(normalizedEmail);
      setCooldownSeconds(RESEND_COOLDOWN_SECONDS);
      setResendStatus("idle");
    } catch (error) {
      setResendStatus("error");
    }
  };

  const isSubmitting = status === "submitting";
  const showSuccess = status === "success";
  const showError = status === "error";
  const isCooldownActive = cooldownSeconds > 0 && email.trim().toLowerCase() === lastRequestEmail;
  const canResend = cooldownSeconds === 0 && resendStatus !== "submitting";
  const countdownLabel = formatCountdown(cooldownSeconds);
  const resendLabel = resendStatus === "submitting"
    ? t("auth.forgotPassword.resendSending", "Resending...")
    : canResend
      ? t("auth.forgotPassword.resendCta", "resend now")
      : t("auth.forgotPassword.resendCountdown", "resend in {time}", { time: countdownLabel });

  const renderSuccessMessage = () => {
    const localized = t(
      "auth.forgotPassword.successMessage",
      "We've sent a password reset link to {email}. The link expires in 30 minutes, so be sure to use it soon.",
      { email }
    );

    if (!email) {
      return localized;
    }

    const emailIndex = localized.indexOf(email);
    if (emailIndex === -1) {
      return localized;
    }

    return (
      <>
        {localized.slice(0, emailIndex)}
        <strong>{email}</strong>
        {localized.slice(emailIndex + email.length)}
      </>
    );
  };

  return (
    <AuthLayout
      eyebrow={t("auth.forgotPassword.eyebrow", "Reset access")}
      title={t("auth.forgotPassword.title", "Recover your Wayli password")}
      lead={t(
        "auth.forgotPassword.lead",
        "Enter the email connected to your account and we'll send you a reset link."
      )}
      illustration={
        <img
          src="/assets/pos_recover.jpg"
          alt={t("auth.media.resetAlt", "Wayli user resetting password")}
          className="auth-illustration"
        />
      }
      asideVariant="solid"
      footer={
        <p>
          {t("auth.forgotPassword.footer.text", "Remember your password?")}{" "}
          <Link to="/login">{t("auth.forgotPassword.footer.link", "Back to login")}</Link>
        </p>
      }
    >
      {showSuccess ? (
        <div className="auth-feedback auth-feedback--success" role="status">
          <h2>{t("auth.forgotPassword.successHeading", "Check your inbox")}</h2>
          <p>{renderSuccessMessage()}</p>
          <p>
            {t(
              "auth.forgotPassword.successRetry",
              "Didn't get the email? Check your spam folder or"
            )}{" "}
            <button
              type="button"
              className="auth-link-inline auth-link-inline--button"
              onClick={handleResend}
              disabled={!canResend}
            >
              {resendLabel}
            </button>
            .
          </p>
          <p>
            <button
              type="button"
              className="auth-link-inline auth-link-inline--button"
              onClick={() => {
                setStatus("idle");
                setResendStatus("idle");
              }}
            >
              {t("auth.forgotPassword.changeEmailCta", "use a different email")}
            </button>
          </p>
          {resendStatus === "error" && (
            <p className="auth-feedback auth-feedback--error" role="alert">
              {t(
                "auth.forgotPassword.errorMessage",
                "Something went wrong. Please try again in a moment."
              )}
            </p>
          )}
        </div>
      ) : (
        <form className="auth-form" onSubmit={handleSubmit}>
          <AuthField
            id="forgot-password-email"
            label={t("auth.fields.email", "Email")}
            type="email"
            name="email"
            autoComplete="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
          />

          {showError && (
            <p className="auth-feedback auth-feedback--error" role="alert">
              {t(
                "auth.forgotPassword.errorMessage",
                "Something went wrong. Please try again in a moment."
              )}
            </p>
          )}

          {isCooldownActive && (
            <p className="auth-feedback" role="status">
              {t(
                "auth.forgotPassword.resendCountdownMessage",
                "You can resend a reset link in {time}.",
                { time: countdownLabel }
              )}
            </p>
          )}

          <button
            type="submit"
            className="auth-button auth-button--primary"
            disabled={isSubmitting || isCooldownActive}
          >
            {isSubmitting
              ? t("auth.forgotPassword.submit.sending", "Sending...")
              : t("auth.forgotPassword.submit.default", "Send reset link")}
          </button>
        </form>
      )}
    </AuthLayout>
  );
};

export default ForgotPasswordPage;
